import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import IdVerifiedBadge from "../components/IdVerifiedBadge";
import JobIdBlockDialog from "../components/JobIdBlockDialog";
import JobDrawer from "../components/JobDrawer";
import JobIdCheckWarning, { useJobIdCheck } from "../components/JobIdCheckWarning";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API_BASE, formatApiError, useAuth } from "../components/AuthContext";
import { Plus, X, Clock, Sparkles, Loader2, Mail, CheckSquare, Upload, Receipt, Filter, FileSignature, GitBranch } from "lucide-react";
import ComposeEmailDialog from "../components/ComposeEmailDialog";
import AssigneePicker, { useTeamMembers } from "../components/AssigneePicker";
import TasksPanel from "../components/TasksPanel";
import JobImportWizard from "../components/JobImportWizard";
import JobStagesDrawer from "../components/JobStagesDrawer";

const STATUSES = [
  { key: "todo", label: "To do", accent: "var(--ink-3)" },
  { key: "in_progress", label: "In progress", accent: "var(--accent)" },
  { key: "review", label: "Review", accent: "var(--moss)" },
  { key: "done", label: "Done", accent: "var(--ink)" },
];
const PRIORITIES = ["low", "medium", "high"];

const PRIO_COLOR = { low: "var(--ink-3)", medium: "var(--moss)", high: "var(--accent)" };

// v68.9 — module-level cache for org statuses + entity types so we don't
// spam the network on every mount.
let _STATUS_CACHE = null;
async function fetchStatusCatalog() {
  if (_STATUS_CACHE) return _STATUS_CACHE;
  try {
    const { data } = await axios.get(`${API_BASE}/workflow-statuses`);
    _STATUS_CACHE = data || [];
  } catch { _STATUS_CACHE = []; }
  return _STATUS_CACHE;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}


export default function Jobs() {
  const { state } = useAuth();
  const isStaff = state?.user?.role === "staff";
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [firmServices, setFirmServices] = useState([]);   // v68.26 — for ITR detection
  // Keep the itrServiceIds set memoised so per-card lookups are O(1).
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [idBlock, setIdBlock] = useState(null);
  const [editJobId, setEditJobId] = useState(() => new URLSearchParams(window.location.search).get("job_id"));
  const [triageId, setTriageId] = useState(null);
  const [composeFor, setComposeFor] = useState(null);
  const [tasksJob, setTasksJob] = useState(null);
  const [wipJob, setWipJob] = useState(null);
  const [stagesJob, setStagesJob] = useState(null);       // v68.16 — eSign inline drawer
  const [orgEsignDefaults, setOrgEsignDefaults] = useState(null);
  // Staff default to My work; owners/managers default to All work.
  const [myWork, setMyWork] = useState(isStaff);
  // v68.9 — filter jobs by owning client's AU entity type
  const [entityFilter, setEntityFilter] = useState("");        // "" = all types
  const [entityTypes, setEntityTypes] = useState([]);          // catalog from /clients/entity-types
  // v68.9 — org's custom status catalog (defaults + custom rows)
  const [orgStatuses, setOrgStatuses] = useState([]);
  // v68.10 — narrow the kanban to jobs with this exact status key (works
  // across defaults + custom). Empty string = show all.
  const [statusFilter, setStatusFilter] = useState("");
  const navigate = useNavigate();
  const members = useTeamMembers();
  const memberById = useMemo(
    () => Object.fromEntries(members.map((m) => [m.user_id, m])),
    [members],
  );

  // Build a lookup: status key → { label, color, column_class }
  const statusByKey = useMemo(() => {
    const map = {};
    for (const s of orgStatuses) {
      map[s.key] = s;
    }
    // Legacy defaults (in case status catalog hasn't loaded yet)
    for (const s of STATUSES) {
      if (!map[s.key]) map[s.key] = { label: s.label, column_class: s.key, color: null };
    }
    return map;
  }, [orgStatuses]);

  // Which column does a given job.status render into?
  const columnOf = (statusKey) => statusByKey[statusKey]?.column_class || statusKey;

  const reload = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (myWork) params.set("assignee_id", "me");
    if (entityFilter) params.set("client_type", entityFilter);
    const url = params.toString() ? `${API_BASE}/jobs?${params}` : `${API_BASE}/jobs`;
    const [j, c, t, s, e] = await Promise.all([
      axios.get(url),
      axios.get(`${API_BASE}/clients`),
      axios.get(`${API_BASE}/workflows`),
      fetchStatusCatalog(),
      entityTypes.length ? Promise.resolve({ data: { types: entityTypes } })
                         : axios.get(`${API_BASE}/clients/entity-types`),
    ]);
    setJobs(j.data); setClients(c.data); setTemplates(t.data);
    setOrgStatuses(s);
    if (!entityTypes.length) setEntityTypes(e.data?.types || []);
    setLoading(false);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [myWork, entityFilter]);

  // v68.16 — load firm-level eSign defaults once (used by JobStagesDrawer)
  useEffect(() => {
    let cancel = false;
    axios.get(`${API_BASE}/firm-services`)
      .then(({ data }) => { if (!cancel) setFirmServices(data || []); })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);
  const itrServiceIds = useMemo(() =>
    new Set((firmServices || [])
      .filter((s) => (s.code || "").toUpperCase() === "ITR")
      .map((s) => s.service_id)),
    [firmServices],
  );
  useEffect(() => {
    let cancel = false;
    axios.get(`${API_BASE}/orgs/me/esign-config`)
      .then((r) => { if (!cancel) setOrgEsignDefaults(r.data); })
      .catch(() => {});
    return () => { cancel = true; };
  }, []);

  const onDrop = async (status) => {
    if (!dragId) return;
    const id = dragId; setDragId(null);
    const current = jobs.find((j) => j.job_id === id);
    if (!current || current.status === status) return;
    // optimistic
    setJobs((rows) => rows.map((r) => (r.job_id === id ? { ...r, status } : r)));
    try {
      await axios.patch(`${API_BASE}/jobs/${id}/status`, { status });
    } catch (err) {
      reload();
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.code === "id_not_verified") {
        setIdBlock({ ...d, retry: async (reason) => {
          await axios.patch(`${API_BASE}/jobs/${id}/status`, { status, id_override_reason: reason });
          toast.success("Job marked done — override recorded");
          reload();
        } });
      } else {
        toast.error(formatApiError(err));
      }
    }
  };

  const clientById = (id) => clients.find((c) => c.client_id === id)?.name || "—";
  const clientEmailById = (id) => clients.find((c) => c.client_id === id)?.primary_contact_email || "";

  const startCompose = (job, e) => {
    e?.stopPropagation();
    const client = clients.find((c) => c.client_id === job.client_id);
    setComposeFor({ job, client });
  };

  const triage = async (job_id, e) => {
    e?.stopPropagation();
    setTriageId(job_id);
    try {
      await axios.post(`${API_BASE}/insights/triage/${job_id}`);
      toast.success("AI triage drafted", {
        description: "View it in Insights.",
        action: { label: "Open", onClick: () => navigate("/insights") },
      });
      window.dispatchEvent(new CustomEvent("insights:refresh-pending"));
    } catch (err) {
      toast.error(formatApiError(err) || "AI triage failed");
    } finally {
      setTriageId(null);
    }
  };

  return (
    <div className="px-10 py-12" data-testid="jobs-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="overline mb-2">Project Management</div>
          <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>Jobs</h1>
          <p className="text-[var(--ink-2)] mt-2">Drag a card between columns to update its status.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMyWork((v) => !v)}
            data-testid="jobs-my-work-toggle"
            className={`text-[11px] inline-flex items-center gap-1 rounded-full px-3 py-1.5 transition ${
              myWork
                ? "bg-[var(--ink)] text-[var(--paper)] border border-[var(--ink)]"
                : "border border-[var(--border)] text-[var(--ink-2)] hover:border-[var(--ink)]"
            }`}
          >
            My work {myWork && <span className="opacity-70">·on</span>}
          </button>
          <div className="relative inline-flex items-center">
            <Filter size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--ink-3)] pointer-events-none" />
            <select
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
              data-testid="jobs-entity-filter"
              className={`text-[11px] appearance-none rounded-full pl-7 pr-6 py-1.5 border transition ${
                entityFilter
                  ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]"
                  : "bg-transparent text-[var(--ink-2)] border-[var(--border)] hover:border-[var(--ink)]"
              }`}
            >
              <option value="">All entity types</option>
              {entityTypes.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
          </div>
          <button onClick={() => setShowImport(true)} className="btn-secondary" data-testid="job-import-btn">
            <Upload size={14} /> Import CSV
          </button>
          <button onClick={() => setShowModal(true)} className="btn-primary" data-testid="job-create-btn">
            <Plus size={14} /> New job
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-[var(--ink-3)]">Loading…</div>
      ) : (
        <>
          {orgStatuses.length > 0 && (
            <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="jobs-status-chip-strip">
              <span className="overline text-[10px] text-[var(--ink-3)] mr-1">Narrow to:</span>
              <button
                onClick={() => setStatusFilter("")}
                data-testid="jobs-status-chip-all"
                className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                  statusFilter === ""
                    ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]"
                    : "border-[var(--border)] text-[var(--ink-2)] hover:border-[var(--ink)]"
                }`}
              >
                All ({jobs.length})
              </button>
              {orgStatuses.map((s) => {
                const count = jobs.filter((j) => j.status === s.key).length;
                if (count === 0 && s.is_default) {
                  // hide empty defaults? no — keep them so click still works
                }
                const active = statusFilter === s.key;
                return (
                  <button key={s.key}
                    onClick={() => setStatusFilter(active ? "" : s.key)}
                    data-testid={`jobs-status-chip-${s.key}`}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition inline-flex items-center gap-1.5 ${
                      active
                        ? "text-[var(--paper)] border-transparent"
                        : "border-[var(--border)] text-[var(--ink-2)] hover:border-[var(--ink)]"
                    }`}
                    style={active ? { background: s.color || "var(--ink)" } : {}}
                  >
                    <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: s.color || "var(--ink-3)" }}/>
                    {s.label} <span className="opacity-70 font-mono">{count}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kanban">
          {STATUSES.map((col) => {
            const items = jobs.filter((j) =>
              columnOf(j.status) === col.key
              && (!statusFilter || j.status === statusFilter),
            );
            return (
              <div
                key={col.key}
                className="bg-[var(--paper)] border border-[var(--border)] rounded-md p-4 min-h-[300px]"
                data-testid={`kanban-col-${col.key}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(col.key)}
              >
                <div className="flex items-center justify-between mb-4 px-1">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: col.accent }} />
                    <div className="overline">{col.label}</div>
                  </div>
                  <div className="text-[12px] font-mono text-[var(--ink-3)]">{items.length}</div>
                </div>
                <div className="space-y-3">
                  {items.map((j) => {
                    const overdue = !!(j.due_at && new Date(j.due_at) < new Date() && columnOf(j.status) !== "done");
                    const hasWorkflow = !!j.template_id;
                    const isITR = (j.client_service_ids || []).some((sid) => itrServiceIds.has(sid))
                      || /individual tax return|\bITR\b/i.test(j.title || "");
                    const unpaidItrStrip = isITR && j.client_payment_status && j.client_payment_status !== "paid";
                    // Precedence for card styling: overdue keeps its red-left; else workflow paints blue.
                    let cardBorder = "border-[var(--border)]";
                    let cardBg;
                    if (overdue) {
                      cardBorder = "border-[var(--accent)] border-l-4";
                      cardBg = { background: "rgba(185, 28, 28, 0.04)" };
                    } else if (hasWorkflow) {
                      cardBorder = "border-[2px] border-[#2563EB]";
                      cardBg = { background: "#EFF6FF" };
                    }
                    return (
                    <div
                      key={j.job_id}
                      draggable
                      onDragStart={() => setDragId(j.job_id)}
                      onDragEnd={() => setDragId(null)}
                      onClick={(e) => { if (!e.target.closest("button,a,select,input,[data-no-open]")) setEditJobId(j.job_id); }}
                      data-testid={`job-card-${j.job_id}`}
                      data-workflow={hasWorkflow ? "1" : "0"}
                      data-unpaid-itr={unpaidItrStrip ? "1" : "0"}
                      className={`bg-white border rounded-md p-3.5 cursor-grab active:cursor-grabbing hover:border-[var(--ink)] transition relative overflow-hidden ${cardBorder}`}
                      style={cardBg}
                      data-overdue={overdue ? "true" : undefined}
                    >
                      {unpaidItrStrip && (
                        <span
                          aria-hidden
                          data-testid={`job-card-${j.job_id}-unpaid-itr-strip`}
                          title="Individual Tax Return · payment outstanding"
                          className="absolute inset-x-0 top-0 h-1"
                          style={{ background: "#DC2626" }}
                        />
                      )}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="badge" style={{ background: PRIO_COLOR[j.priority] || "var(--ink-3)", color: "var(--paper)" }}>
                            {j.priority}
                          </span>
                          {statusByKey[j.status] && !["todo","in_progress","review","done"].includes(j.status) && (
                            <span className="badge text-[9px]"
                              style={{ background: statusByKey[j.status].color || "var(--ink-3)", color: "var(--paper)" }}
                              data-testid={`job-status-badge-${j.job_id}`}>
                              {statusByKey[j.status].label}
                            </span>
                          )}
                        </div>
                        <span className="text-[10px] text-[var(--ink-3)] font-mono">
                          {j.due_at && new Date(j.due_at) < new Date() && columnOf(j.status) !== "done" && (
                            <span className="text-[var(--accent)] font-medium mr-1" data-testid={`job-overdue-badge-${j.job_id}`}>OVERDUE ·</span>
                          )}
                          {fmtDate(j.due_at)}
                        </span>
                      </div>
                      <div className="text-[14px] text-[var(--ink)] font-medium leading-snug mb-2">{j.title}</div>
                      <div className="text-[11px] text-[var(--ink-3)] font-mono truncate">{clientById(j.client_id)}</div>
                      {hasWorkflow && (
                        <div
                          className="mt-2 inline-flex items-center gap-1 text-[10px] font-medium rounded-full px-2 py-0.5"
                          style={{ background: "#DBEAFE", color: "#1E40AF" }}
                          data-testid={`job-card-${j.job_id}-workflow-badge`}
                        >
                          <GitBranch size={9} />
                          <span className="truncate max-w-[160px]">{j.template_name || "Workflow"}</span>
                        </div>
                      )}
                      <div className="mt-2" onMouseDown={(e) => e.stopPropagation()} draggable={false}>
                        <AssigneePicker
                          value={j.assignee_id}
                          onChange={async (uid) => {
                            try {
                              await axios.patch(`${API_BASE}/jobs/${j.job_id}`, { assignee_id: uid });
                              setJobs((rows) => rows.map((r) => r.job_id === j.job_id ? { ...r, assignee_id: uid } : r));
                              toast.success(uid ? `Assigned to ${memberById[uid]?.name || "teammate"}` : "Unassigned");
                            } catch (err) { toast.error(formatApiError(err)); }
                          }}
                          testid={`job-assignee-${j.job_id}`}
                          compact
                          label="Assign"
                        />
                      </div>
                      <div className="text-[11px] text-[var(--ink-3)] mt-2 flex items-center justify-between gap-3">
                        <span className="flex items-center gap-1"><Clock size={11} /> {j.budget_minutes}m</span>
                        <div className="flex items-center gap-3">
                          {j.client_id && clientEmailById(j.client_id) && (
                            <button
                              onClick={(e) => startCompose(j, e)}
                              onMouseDown={(e) => e.stopPropagation()}
                              draggable={false}
                              data-testid={`job-compose-${j.job_id}`}
                              className="inline-flex items-center gap-1 text-[var(--ink-2)] hover:text-[var(--ink)]"
                              title={`Email ${clientEmailById(j.client_id)}`}
                            >
                              <Mail size={10} /> Email
                            </button>
                          )}
                          <button
                            onClick={(e) => triage(j.job_id, e)}
                            onMouseDown={(e) => e.stopPropagation()}
                            draggable={false}
                            disabled={triageId === j.job_id}
                            data-testid={`job-ask-ai-${j.job_id}`}
                            className="inline-flex items-center gap-1 text-[var(--accent)] hover:underline disabled:opacity-50"
                          >
                            {triageId === j.job_id ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                            Ask AI
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setTasksJob(j); }}
                            onMouseDown={(e) => e.stopPropagation()}
                            draggable={false}
                            data-testid={`job-tasks-${j.job_id}`}
                            className="inline-flex items-center gap-1 text-[var(--ink-2)] hover:text-[var(--ink)]"
                            title="Tasks"
                          >
                            <CheckSquare size={10} /> Tasks
                          </button>
                          {(j.stages || []).length > 0 && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setStagesJob(j); }}
                              onMouseDown={(e) => e.stopPropagation()}
                              draggable={false}
                              data-testid={`job-stages-${j.job_id}`}
                              className="inline-flex items-center gap-1 text-[var(--ink-2)] hover:text-[var(--ink)]"
                              title="Stages · eSign"
                            >
                              <FileSignature size={10} /> Stages
                            </button>
                          )}
                          {!isStaff && j.client_id && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setWipJob(j); }}
                              onMouseDown={(e) => e.stopPropagation()}
                              draggable={false}
                              data-testid={`job-bill-wip-${j.job_id}`}
                              className="inline-flex items-center gap-1 text-[var(--accent)] hover:underline"
                              title="Create invoice from unbilled time"
                            >
                              <Receipt size={10} /> Bill WIP
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                  {items.length === 0 && <div className="text-[12px] text-[var(--ink-3)] text-center py-6">Drop here</div>}
                </div>
              </div>
            );
          })}
        </div>
        </>
      )}

      {showModal && (
        <JobModal
          clients={clients}
          templates={templates}
          onClose={() => setShowModal(false)}
          onCreated={() => { setShowModal(false); reload(); }}
        />
      )}
      {editJobId && (
        <JobDrawer jobId={editJobId} clients={clients} templates={templates}
          statuses={Object.entries(statusByKey)
            .map(([key, s]) => ({ key, label: s.label || key, rank: { todo: 0, in_progress: 1, review: 2, done: 9 }[s.column_class] ?? 5 }))
            .sort((a, b) => a.rank - b.rank)}
          onClose={() => { setEditJobId(null); if (window.location.search.includes("job_id")) window.history.replaceState(null, "", window.location.pathname); }}
          onSaved={reload} />
      )}
      {idBlock && <JobIdBlockDialog info={idBlock} onClose={() => setIdBlock(null)} onOverride={idBlock.retry} />}
      <ComposeEmailDialog
        open={!!composeFor}
        onClose={() => setComposeFor(null)}
        onSent={() => setComposeFor(null)}
        context={{ job_id: composeFor?.job?.job_id, client_id: composeFor?.client?.client_id }}
        defaultTo={composeFor?.client?.primary_contact_email || ""}
        defaultToName={composeFor?.client?.name || composeFor?.job?.title || ""}
      />
      <JobImportWizard
        open={showImport}
        onClose={() => { setShowImport(false); reload(); }}
        onImported={() => { reload(); }}
      />
      {tasksJob && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/30"
          onClick={() => setTasksJob(null)}
          data-testid="job-tasks-modal"
        >
          <div
            className="w-full sm:max-w-xl rounded-t-lg sm:rounded-lg shadow-xl"
            style={{ background: "var(--paper)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: "var(--border)" }}>
              <div>
                <div className="overline">Tasks · job</div>
                <div className="text-[14px] font-medium text-[var(--ink)]" data-testid="job-tasks-modal-title">{tasksJob.title}</div>
              </div>
              <button onClick={() => setTasksJob(null)} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="job-tasks-modal-close">
                <X size={18}/>
              </button>
            </div>
            <div className="p-5 max-h-[70vh] overflow-y-auto">
              <TasksPanel parentKind="job" parentId={tasksJob.job_id} title="Tasks for this job" />
            </div>
          </div>
        </div>
      )}
      {wipJob && (
        <BillWipModal job={wipJob} onClose={() => setWipJob(null)}
                      onDone={() => { setWipJob(null); reload(); }} />
      )}
      {stagesJob && (
        <JobStagesDrawer
          job={stagesJob}
          onClose={() => setStagesJob(null)}
          orgDefaults={orgEsignDefaults}
        />
      )}
    </div>
  );
}

function BillWipModal({ job, onClose, onDone }) {
  const navigate = useNavigate();
  const [entries, setEntries] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dueInDays, setDueInDays] = useState(14);
  const [rateOverride, setRateOverride] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/time-entries?job_id=${job.job_id}`);
        const unbilled = (r.data || []).filter(
          (e) => e.billable && (e.invoice_id == null || e.invoice_id === undefined)
        );
        if (alive) setEntries(unbilled);
      } catch (e) {
        if (alive) setEntries([]);
        toast.error(formatApiError(e));
      }
    })();
    return () => { alive = false; };
  }, [job.job_id]);

  const totalMin = (entries || []).reduce((s, e) => s + (e.minutes || 0), 0);
  const totalHrs = totalMin / 60;

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { job_id: job.job_id, due_in_days: Number(dueInDays) || 14 };
      if (rateOverride && parseFloat(rateOverride) > 0) {
        payload.hourly_rate_override = parseFloat(rateOverride);
      }
      const r = await axios.post(`${API_BASE}/invoices/from-wip`, payload);
      toast.success(`Invoice created · $${r.data.total} · ${r.data.lines_count} line(s)`);
      onDone();
      navigate(`/invoices`);
    } catch (e) {
      toast.error(formatApiError(e) || "Could not create invoice");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
         onClick={onClose} data-testid="bill-wip-modal">
      <div className="w-full max-w-lg rounded-lg shadow-xl"
           style={{ background: "var(--paper)" }}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: "var(--border)" }}>
          <div>
            <div className="overline">Bill WIP · {job.title}</div>
            <div className="text-[13px] text-[var(--ink-3)]">Turn unbilled billable time into a draft invoice</div>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]"
                  data-testid="bill-wip-close">
            <X size={18}/>
          </button>
        </div>
        <div className="p-5 space-y-3">
          {entries === null ? (
            <div className="text-[12px] text-[var(--ink-3)]"><Loader2 className="animate-spin inline mr-1" size={12}/> Loading time entries…</div>
          ) : entries.length === 0 ? (
            <div className="text-[13px] text-[var(--ink-2)] p-4 bg-[var(--subtle)] rounded-md"
                 data-testid="bill-wip-empty">
              No unbilled billable time on this job. Log some time first, then come back.
            </div>
          ) : (
            <>
              <div className="text-[13px] text-[var(--ink-2)]" data-testid="bill-wip-summary">
                <span className="font-medium">{entries.length}</span> unbilled time entr{entries.length === 1 ? "y" : "ies"}{" "}
                · <span className="font-medium">{totalHrs.toFixed(1)}h</span> total
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-[11px] text-[var(--ink-3)]">
                  Due in days
                  <input type="number" min="0" max="365" value={dueInDays}
                         onChange={(e) => setDueInDays(e.target.value)}
                         className="mt-1 w-full bg-white border border-[var(--border)] rounded-md px-2 py-1.5 text-[12px]"
                         data-testid="bill-wip-due-days"/>
                </label>
                <label className="text-[11px] text-[var(--ink-3)]">
                  Override rate $/hr (optional)
                  <input type="number" min="0" step="1" value={rateOverride}
                         onChange={(e) => setRateOverride(e.target.value)}
                         placeholder="Uses each staff's hourly_rate"
                         className="mt-1 w-full bg-white border border-[var(--border)] rounded-md px-2 py-1.5 text-[12px]"
                         data-testid="bill-wip-rate-override"/>
                </label>
              </div>
              <div className="text-[10px] text-[var(--ink-3)]">
                The invoice is created as a draft. Open the Invoices page to review and send.
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-end gap-2"
             style={{ borderColor: "var(--border)" }}>
          <button onClick={onClose} className="text-[12px] text-[var(--ink-3)] hover:text-[var(--ink)]"
                  data-testid="bill-wip-cancel">Cancel</button>
          <button onClick={submit} disabled={busy || !entries || entries.length === 0}
                  className="btn-primary text-[12px] disabled:opacity-50"
                  data-testid="bill-wip-create">
            {busy ? <Loader2 size={12} className="animate-spin"/> : <Receipt size={12}/>}
            Create draft invoice
          </button>
        </div>
      </div>
    </div>
  );
}

export function JobModal({ clients, templates, onClose, onCreated, defaultClientId }) {
  const [tplId, setTplId] = useState("");
  const [form, setForm] = useState({
    title: "", client_id: defaultClientId || clients[0]?.client_id || "", status: "todo",
    due_at: "", budget_minutes: 60, priority: "medium", description: "",
  });
  const [error, setError] = useState(""); const [saving, setSaving] = useState(false);
  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const idCheck = useJobIdCheck(form.client_id);
  useEffect(() => { setError(""); }, [form.client_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (idCheck.blocked) { setError("This client isn't ID verified — send an ID check, or tick 'Continue creating the job anyway'."); return; }
    setError(""); setSaving(true);
    try {
      if (tplId) {
        // Spawn from template
        const body = {
          template_id: tplId,
          client_id: form.client_id || undefined,
          priority: form.priority,
        };
        if (form.title) body.title = form.title;
        if (form.due_at) body.due_at = new Date(form.due_at).toISOString();
        await axios.post(`${API_BASE}/workflows/${tplId}/apply`, body);
      } else {
        const payload = { ...form, budget_minutes: Number(form.budget_minutes) };
        if (form.due_at) payload.due_at = new Date(form.due_at).toISOString();
        else delete payload.due_at;
        if (!payload.client_id) delete payload.client_id;
        if (!payload.description) delete payload.description;
        await axios.post(`${API_BASE}/jobs`, payload);
      }
      onCreated();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="job-modal"
         style={{ background: "rgba(26,24,20,0.45)" }}>
      <div className="bg-[var(--paper)] border border-[var(--border)] rounded-md max-w-lg w-full p-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="overline">New job</div>
            <h3 className="font-heading text-2xl mt-1" style={{ fontWeight: 500 }}>Create a job</h3>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="job-modal-close"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="job-form">
          {templates.length > 0 && (
            <div className="bg-[var(--subtle)] border border-[var(--border)] rounded-md p-3">
              <label className="overline block mb-1.5">Start from template <span className="text-[var(--ink-3)] normal-case tracking-normal">(optional · applies stages + automators)</span></label>
              <select value={tplId} onChange={(e) => setTplId(e.target.value)} data-testid="job-form-template"
                className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]">
                <option value="">— blank job —</option>
                {templates.map((t) => <option key={t.template_id} value={t.template_id}>{t.name} ({t.stages?.length || 0} stages · {t.automators?.length || 0} automators)</option>)}
              </select>
            </div>
          )}
          <Field label={tplId ? "Override title (optional)" : "Title"} value={form.title} onChange={upd("title")} testid="job-form-title" required={!tplId} />
          <Select label="Client" value={form.client_id} onChange={upd("client_id")} testid="job-form-client">
            <option value="">— none —</option>
            {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}{c.voi_status === "APPROVED" ? " ✓ ID verified" : ""}</option>)}
          </Select>
          {form.client_id && (
            <div className="-mt-1"><IdVerifiedBadge client={clients.find((c) => c.client_id === form.client_id)} showUnverified testid="job-form-client-id-badge" /></div>
          )}
          {form.client_id && <JobIdCheckWarning clientId={form.client_id} state={idCheck} />}
          <div className="grid grid-cols-2 gap-3">
            <Select label="Status" value={form.status} onChange={upd("status")} testid="job-form-status">
              {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </Select>
            <Select label="Priority" value={form.priority} onChange={upd("priority")} testid="job-form-priority">
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field type="date" label="Due date" value={form.due_at} onChange={upd("due_at")} testid="job-form-due" />
            <Field type="number" label="Budget (min)" value={form.budget_minutes} onChange={upd("budget_minutes")} testid="job-form-budget" />
          </div>
          {error && <div className="text-[13px] text-[var(--accent)]" data-testid="job-form-error">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center" data-testid="job-form-submit">
              {saving ? "Saving…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      <input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]" />
    </div>
  );
}
function Select({ label, value, onChange, children, testid }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]">
        {children}
      </select>
    </div>
  );
}
